import { ArrowUpDown, SlidersHorizontal, X, Download, FolderInput, Share2, Trash2, RotateCcw } from 'lucide-react';
import Menu, { MenuItem, MenuLabel, MenuSeparator } from '../common/Menu';
import { FILE_TYPE_GROUPS } from '../../utils/fileTypes';
import cn from '../../utils/cn';

const SORTS = [
  { key: 'name:asc', label: 'Name (A–Z)' },
  { key: 'name:desc', label: 'Name (Z–A)' },
  { key: 'modified:desc', label: 'Newest first' },
  { key: 'modified:asc', label: 'Oldest first' },
  { key: 'size:desc', label: 'Largest first' },
  { key: 'size:asc', label: 'Smallest first' },
];

/** Selection mode replaces the toolbar entirely. Only permitted actions render. */
export function SelectionBar({
  count,
  onClear,
  onDownload,
  onMove,
  onShare,
  onDelete,
  onRestore,
  onPurge,
  canDownload,
  canMove,
  canShare,
  canDelete,
  canRestore,
  canPurge,
}) {
  return (
    <div className="selection-bar" role="toolbar" aria-label="Bulk actions">
      <span className="selection-bar__count">{count} selected</span>
      {/* Trash first: a deleted document cannot be downloaded, moved or deleted again, and
          offering those was the bug — every one of them returned "Document not found". */}
      {canRestore && (
        <button type="button" className="link" onClick={onRestore}>
          <RotateCcw size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
          Restore
        </button>
      )}
      {canPurge && (
        <button type="button" className="link" onClick={onPurge} style={{ color: 'var(--err-fg)' }}>
          <Trash2 size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
          Delete permanently
        </button>
      )}
      {canDownload && (
        <button type="button" className="link" onClick={onDownload}>
          <Download size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
          Download
        </button>
      )}
      {canMove && (
        <button type="button" className="link" onClick={onMove}>
          <FolderInput size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
          Move
        </button>
      )}
      {canShare && (
        <button type="button" className="link" onClick={onShare}>
          <Share2 size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
          Share
        </button>
      )}
      {canDelete && (
        <button type="button" className="link" onClick={onDelete} style={{ color: 'var(--err-fg)' }}>
          <Trash2 size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
          Delete
        </button>
      )}
      <span className="toolbar__spacer" />
      <button type="button" className="btn--icon" onClick={onClear} aria-label="Clear selection">
        <X size={16} />
      </button>
    </div>
  );
}

/** v2 browser controls: List | Grid segmented switch, plus sort and type filter. */
export default function DocumentToolbar({ viewMode, onViewModeChange, sort, onSortChange, typeFilter, onTypeFilterChange }) {
  const activeSort = SORTS.find((s) => s.key === sort);
  return (
    <div className="toolbar">
      <Menu
        align="start"
        width={188}
        trigger={
          <button type="button" className="btn btn-g">
            <SlidersHorizontal size={15} />
            {typeFilter ? FILE_TYPE_GROUPS.find((g) => g.key === typeFilter)?.label : 'All types'}
          </button>
        }
      >
        <MenuLabel>File type</MenuLabel>
        <MenuItem onClick={() => onTypeFilterChange(null)}>All types</MenuItem>
        <MenuSeparator />
        {FILE_TYPE_GROUPS.map((g) => (
          <MenuItem key={g.key} onClick={() => onTypeFilterChange(g.key)}>
            {g.label}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        align="start"
        width={188}
        trigger={
          <button type="button" className="btn btn-g">
            <ArrowUpDown size={15} />
            {activeSort?.label || 'Sort'}
          </button>
        }
      >
        <MenuLabel>Sort by</MenuLabel>
        {SORTS.map((s) => (
          <MenuItem key={s.key} onClick={() => onSortChange(s.key)}>
            {s.label}
          </MenuItem>
        ))}
      </Menu>

      <div className="seg" role="group" aria-label="View">
        <button type="button" className={cn(viewMode === 'list' && 'is-active')} onClick={() => onViewModeChange('list')}>
          List
        </button>
        <button type="button" className={cn(viewMode === 'grid' && 'is-active')} onClick={() => onViewModeChange('grid')}>
          Grid
        </button>
      </div>
    </div>
  );
}
